import React, { useCallback, useState } from 'react';
import { Box } from '../../CommonComponents/OPL/Box/Box';
import { Modal } from '../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../../CommonComponents/OPL/Modal/ModalFooter';
import { ModalHeader } from '../../CommonComponents/OPL/Modal/ModalHeader';
import { Button } from '../../CommonComponents/OPL/Button/Button';
import { Text } from '../../CommonComponents/OPL/Text/Text';
import { WizardStepper } from './components/WizardStepper';
import { UploadPlanStep } from './steps/UploadPlanStep';
import { ReviewDifferencesStep } from './steps/ReviewDifferencesStep';
import { FinalizeImportStep } from './steps/FinalizeImportStep';
import { useCancelReconciliationMutation } from './api/useCancelReconciliationMutation';
import { refreshClientDataAfterPdfImport } from './refreshClientDataAfterPdfImport';
import {
  Decisions,
  ReconciliationProposal,
  ReconciliationStatusResult,
} from './pdfReconciliationTypes';

interface Props {
  isOpen: boolean;
  clientId: number;
  onClose: () => void;
}

type WizardStep = 0 | 1 | 2;

export const PdfReconciliationWizard = ({ isOpen, clientId, onClose }: Props) => {
  const [step, setStep] = useState<WizardStep>(0);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [itemId, setItemId] = useState<string | null>(null);
  const [proposal, setProposal] = useState<ReconciliationProposal | null>(null);
  const [decisions, setDecisions] = useState<Decisions>({});
  const [continueReady, setContinueReady] = useState(false);
  const [isApplied, setIsApplied] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  const isNavigationLocked = isApplied || isApplying;

  const { mutateAsync: cancel } = useCancelReconciliationMutation();

  const handleClose = useCallback(async () => {
    if (isApplying) {
      return;
    }

    const shouldRefreshClientData = isApplied;

    if (batchId && step !== 2 && !isApplied) {
      try {
        await cancel({ batchId });
      } catch (err) {
        console.error(err);
      }
    }

    setStep(0);
    setBatchId(null);
    setItemId(null);
    setProposal(null);
    setDecisions({});
    setContinueReady(false);
    setIsApplied(false);
    setIsApplying(false);
    onClose();

    if (shouldRefreshClientData) {
      void refreshClientDataAfterPdfImport(clientId);
    }
  }, [batchId, cancel, clientId, isApplied, isApplying, onClose, step]);

  const handleRequestClose = useCallback(() => {
    if (isNavigationLocked) {
      return;
    }

    handleClose();
  }, [handleClose, isNavigationLocked]);

  const handleUploaded = useCallback((newBatchId: string, newItemId: string) => {
    setBatchId(newBatchId);
    setItemId(newItemId);
    setStep(1);
  }, []);

  const handleProposalLoaded = useCallback((data: ReconciliationStatusResult) => {
    if (data.proposal) {
      setProposal(data.proposal);
    }
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleRequestClose}
      size="xl"
      shouldCloseOnOverlayClick={false}
      aria={{ labelledby: 'pdf-reconciliation-wizard' }}
    >
      <ModalHeader as="h2" id="pdf-reconciliation-wizard">
        Import from PDF
      </ModalHeader>

      {!isNavigationLocked && <ModalCloseButton onClick={handleClose} />}

      <ModalBody>
        <Box mb="24">
          <WizardStepper current={step} />
        </Box>

        {step === 0 && <UploadPlanStep clientId={clientId} onUploaded={handleUploaded} />}

        {step === 1 && batchId && itemId && (
          <ReviewDifferencesStep
            batchId={batchId}
            itemId={itemId}
            decisions={decisions}
            onDecisionsChange={setDecisions}
            onProposalLoaded={handleProposalLoaded}
            onContinueReady={setContinueReady}
          />
        )}

        {step === 2 && batchId && itemId && proposal && (
          <FinalizeImportStep
            batchId={batchId}
            itemId={itemId}
            clientId={clientId}
            decisions={decisions}
            proposal={proposal}
            onApplied={() => setIsApplied(true)}
            onApplyingChange={setIsApplying}
            onClose={handleClose}
          />
        )}
      </ModalBody>

      {!isNavigationLocked && (
        <ModalFooter>
          <Button variant="subtleInactive" onClick={handleClose}>
            Cancel
          </Button>
          {step === 1 && (
            <Button ml="8" disabled={!continueReady || !proposal} onClick={() => setStep(2)}>
              Continue to finalize
            </Button>
          )}

          {step === 2 && (
            <Button ml="8" variant="subtleInactive" onClick={() => setStep(1)}>
              Back
            </Button>
          )}

          {step === 0 && (
            <Text fontSize="small" color="neutral.500" ml="auto">
              We'll match the imported plan against this household for review.
            </Text>
          )}
        </ModalFooter>
      )}
    </Modal>
  );
};
